import { z } from 'zod';
import type { PlatformInvitationCreateRequest, PlatformOrgUpdateRequest } from '@hangar/shared';
import { api } from './client';
import { ClientOrg, ClientOrgDeletion, Invitation, InvitationList } from './schemasMembers';

/* ---------- Client lifecycle (SPEC decision 26, §17.6): `/platform/orgs/{orgId}/…`, platform operators only ---------- */

const enc = encodeURIComponent;
const NoContent = z.null();
const base = (orgId: string) => `/platform/orgs/${enc(orgId)}`;

/** `PATCH /platform/orgs/{orgId}`: the name and/or the email domains (the slug never changes). */
export const updateClientOrg = (orgId: string, body: PlatformOrgUpdateRequest): Promise<ClientOrg> => api('PATCH', base(orgId), { schema: ClientOrg, body });

/** The client's open invitations, newest first (one page of 200). */
export const listClientInvitations = (orgId: string): Promise<Invitation[]> =>
  api('GET', `${base(orgId)}/invitations`, { schema: InvitationList, query: { status: 'pending', limit: 200 } }).then((p) => p.data.filter((i) => i.status === 'pending'));
export const inviteToClient = (orgId: string, body: PlatformInvitationCreateRequest, idempotencyKey: string): Promise<Invitation> =>
  api('POST', `${base(orgId)}/invitations`, { schema: Invitation, body, idempotencyKey });
export const resendClientInvitation = (orgId: string, invitationId: string): Promise<Invitation> =>
  api('POST', `${base(orgId)}/invitations/${enc(invitationId)}/resend`, { schema: Invitation });
export const cancelClientInvitation = (orgId: string, invitationId: string): Promise<null> =>
  api('DELETE', `${base(orgId)}/invitations/${enc(invitationId)}`, { schema: NoContent });

/** `POST …/suspend {reason}`: nobody in the client can use Hangar until it is reactivated. */
export const suspendClient = (orgId: string, reason: string): Promise<ClientOrg> => api('POST', `${base(orgId)}/suspend`, { schema: ClientOrg, body: { reason } });
/** `POST …/reactivate` (409 while a deletion exists). */
export const reactivateClient = (orgId: string): Promise<ClientOrg> => api('POST', `${base(orgId)}/reactivate`, { schema: ClientOrg });
/** `POST …/deletion {confirmSlug}`: runs (or resumes) the deletion of a suspended client. */
export const deleteClient = (orgId: string, confirmSlug: string): Promise<ClientOrgDeletion> =>
  api('POST', `${base(orgId)}/deletion`, { schema: ClientOrgDeletion, body: { confirmSlug } });
